import React from "react";
import { Button, Card, Popup, Header } from "semantic-ui-react";
import { Organization } from "../../../../types/organizations";
import { textInputRender } from "../../../../utils/components";
import { validateEmailString } from "../../../../utils/formValidation";
import ConfirmationPrompt from "../../../common/ConfirmationPrompt";
import FormModal from "../../../common/FormModal";

type ManageOrganizationAndContactProps = {
  /** Function to run on edit organization action trigger.*/
  editOrganization: (organization: any) => any;
  /** Function to run on edit organization contact action trigger.*/
  editOrganizationContact: (id: number, contact: any) => any;
  /** Organization object to be displayed. */
  organizationEntity: Organization | Record<string, never>;
  /** Organization contact object to be displayed. */
  organizationContacts: Record<string, any>[];
  /** Main region name. Note: Unused */
  mainRegionName?: string;
  /** Is user allowed to edit?*/
  canEdit: boolean;
  /** Is user allowed to remove?*/
  canRemove: boolean;
  /** Function to run on remove organization contact action trigger.*/
  removeOrganizationsContact: (id: number) => any;
  /** Is disabled? */
  disabled: boolean;
};
class ManageOrganizationAndContact extends React.PureComponent<ManageOrganizationAndContactProps> {
  state = { openRemoveContact: false };
  popupContactContent = (organizationContact: Record<string, any>) => (
    <>
      {organizationContact.email && (
        <>
          <Header>Email:</Header>
          <p>{organizationContact.email}</p>
        </>
      )}
      {organizationContact.phone && (
        <>
          <Header>Phone:</Header>
          <p>{organizationContact.phone}</p>
        </>
      )}
    </>
  );
  popupOrganizationContent = () => (
    <>
      <Header>Address:</Header>
      {this.props.organizationEntity.street && <b>{this.props.organizationEntity.street}</b>}
      {this.props.organizationEntity.city && <div>{this.props.organizationEntity.city}</div>}
      {this.props.organizationEntity.state && <div>{this.props.organizationEntity.state}</div>}
      {this.props.organizationEntity.zip && <div>{this.props.organizationEntity.zip}</div>}
      {this.props.organizationEntity.country && <div>{this.props.organizationEntity.country}</div>}
    </>
  );
  render = () => (
    <>
      <Card>
        <Card.Content>
          <Card.Header>
            {this.props.organizationEntity.name}
            {(this.props.organizationEntity.street ||
              this.props.organizationEntity.city ||
              this.props.organizationEntity.state ||
              this.props.organizationEntity.zip ||
              this.props.organizationEntity.country) && (
              <Popup
                hoverable
                pinned
                content={this.popupOrganizationContent()}
                trigger={
                  <Button
                    aria-label="Organization information button"
                    style={{ backgroundColor: "transparent", float: "right" }}
                    size="mini"
                    icon="info circle"
                  />
                }
              />
            )}
          </Card.Header>
        </Card.Content>
        {this.props.canEdit && (
          <Card.Content extra>
            <FormModal
              size="mini"
              modalHeader="Edit organization information"
              modalTrigger={onClick => (
                <Button fluid basic color="yellow" onClick={onClick} disabled={this.props.disabled}>
                  Change
                </Button>
              )}
              fields={[
                {
                  name: "name",
                  render: textInputRender("Name", "Name", true),
                  required: true,
                  initialValue: this.props.organizationEntity.name || ""
                },
                {
                  name: "street",
                  render: textInputRender("Street", "Street"),
                  initialValue: this.props.organizationEntity.street || ""
                },
                {
                  name: "city",
                  render: textInputRender("City", "City"),
                  initialValue: this.props.organizationEntity.city || ""
                },
                {
                  name: "state",
                  render: textInputRender("State / Province", "State / Province"),
                  initialValue: this.props.organizationEntity.state || ""
                },
                {
                  name: "zip",
                  render: textInputRender("Zip / Postal code", "Zip / Postal code"),
                  initialValue: this.props.organizationEntity.zip || ""
                },
                {
                  name: "country",
                  render: textInputRender("Country", "Country"),
                  initialValue: this.props.organizationEntity.country || ""
                }
              ]}
              onSubmit={organizationInformation =>
                this.props.editOrganization(organizationInformation)
              }
            />
          </Card.Content>
        )}
      </Card>
      <Header as="h4"> Contact </Header>
      <Card.Group className="organization-contact">
        {this.props.organizationContacts.map(organizationContact => (
          <Card style={{ marginRight: "5%" }} key={organizationContact.id}>
            <Card.Content>
              <Card.Header>
                {organizationContact.name}
                <Popup
                  hoverable
                  pinned
                  content={this.popupContactContent(organizationContact)}
                  trigger={
                    <Button
                      aria-label="Organization contact information button"
                      style={{ backgroundColor: "transparent", float: "right" }}
                      size="mini"
                      icon="info circle"
                    />
                  }
                />
              </Card.Header>
              <Card.Description>
                {organizationContact.jobTitle} <br />
              </Card.Description>
            </Card.Content>
            {(this.props.canEdit || this.props.canRemove) && (
              <Card.Content extra>
                <Button.Group fluid>
                  {this.props.canEdit && (
                    <FormModal
                      size="mini"
                      modalHeader="Edit contact information"
                      fields={[
                        {
                          name: "name",
                          render: textInputRender("Name", "Name", true),
                          required: true,
                          initialValue: organizationContact.name || ""
                        },
                        {
                          name: "jobTitle",
                          render: textInputRender("Job title", "Job title", true),
                          required: true,
                          initialValue: organizationContact.jobTitle || ""
                        },
                        {
                          name: "email",
                          render: textInputRender("Email", "example@email.com", true),
                          required: true,
                          validators: [validateEmailString],
                          initialValue: organizationContact.email || ""
                        },
                        {
                          name: "phone",
                          render: textInputRender("Phone", "Phone"),
                          initialValue: organizationContact.phone || ""
                        }
                      ]}
                      onSubmit={contactInformation =>
                        this.props.editOrganizationContact(
                          organizationContact.id,
                          contactInformation
                        )
                      }
                      modalTrigger={onClick => (
                        <Button
                          basic
                          color="yellow"
                          onClick={onClick}
                          disabled={this.props.disabled}
                        >
                          Change
                        </Button>
                      )}
                    />
                  )}
                  {this.props.canRemove && (
                    <ConfirmationPrompt
                      trigger={
                        <Button basic color="red">
                          Remove
                        </Button>
                      }
                      buttonLabel={"Remove"}
                      buttonColor={"red"}
                      headerIcon={"trash"}
                      onConfirmation={() =>
                        this.props.removeOrganizationsContact(organizationContact.id)
                      }
                      content={"Are you sure you want to remove this contact?"}
                    />
                  )}
                </Button.Group>
              </Card.Content>
            )}
          </Card>
        ))}
      </Card.Group>
    </>
  );
}

export default ManageOrganizationAndContact;
